import { useState, useEffect } from 'react';
import _ from 'lodash';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FcList, FcGrid } from 'react-icons/fc';
import { BiSolidFolderPlus, BiLogoYoutube } from 'react-icons/bi';
import {
	Dropdown,
	Col,
	Button,
	Breadcrumb,
	Container,
	Form,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import {
	AreaObjectTypeEnum,
	AreaEditObjectDTO,
	AreaFolderDTO,
	AreaObjectCreateRequestDTO,
	RoleEnum,
} from '../../../api';
import toaster from '../../../utils/toaster';
import Restricted from '../../../components/Restricted/Restricted';
import DisplayData from '../../../components/Areas/DisplayData';
import OptionActionModal from '../../../components/modals/optionActionModal';
import { useApiClient } from '../../../hooks/useApiClient';
import { GTMsendEvent } from '../../../utils/trackingGA4';
import '../areas.scss';

interface Filters {
	firstCatergory: string;
	secondCatergory: string;
	thirdCatergory: string;
	fourthCatergory: string;
}
interface FolderEdit {
	is_visible: boolean;
	id: number;
}

const initialBreadcrumbs = [];

function AreaMarketing() {
	const [modalType, setModalType] = useState('');
	const [modalParams, setModalParams] = useState(null);
	const [isModalVisibile, setModalVisibility] = useState(false);
	const [showAllBreadcrumbs, setShowAllBreadcrumbs] = useState(false);
	const [viewFolderEdit, setViewFolderEdit] = useState<FolderEdit>(
		{} as FolderEdit
	);
	const [loading, setLoading] = useState(false);
	const [loadingSearch, setLoadingSearch] = useState(false);
	const [isSearchActive, setIsSearchActive] = useState(false);
	const [search, setSearch] = useState('');
	const [searchResult, setSearchResult] = useState([]);
	const [error, setError] = useState(null);
	const [currentFolder, setCurrentFolder] = useState<AreaFolderDTO>(
		{} as AreaFolderDTO
	);
	const [breadcrumbItems, setBreadcrumbsItems] =
		useState<{ id: string; title: string }[]>(initialBreadcrumbs);
	const viewGridString = localStorage.getItem('viewGrid');
	const [viewGrid, setViewGrid] = useState<Boolean>(
		viewGridString !== null ? JSON.parse(viewGridString) : true
	);
	const { t } = useTranslation();

	const { apiClient, handleApi } = useApiClient();
	const navigate = useNavigate();
	let { id } = useParams();

	const showModal = (type, params?: any) => {
		setModalType(type);
		setModalParams(params);
		setModalVisibility(true);
	};

	const closeModal = () => {
		setModalType('');
		setModalVisibility(false);
	};

	// API calls from onSubmitFn in optionActionsModal
	const createAreaMarketingFolder = (values) => {
		const body = {} as AreaObjectCreateRequestDTO;
		body.title = values.folderName;
		body.parent_folder_id = currentFolder.id;
		body.type = AreaObjectTypeEnum.FOLDER;

		return handleApi(apiClient.areaMarketingController.postFolder(body));
	};

	const editAreaMarketingFolder = (values) => {
		const body: AreaEditObjectDTO = {} as AreaEditObjectDTO;
		body.title = values.folderName;

		return handleApi(
			apiClient.areaMarketingController.editFolderName(values.id, body)
		);
	};

	const editAreaMarketingVideo = (values) => {
		const body = values;
		body.title = values.videoTitle;
		body.link = values.videoLink;

		return handleApi(
			apiClient.areaMarketingController.editFolderName(values.id, body)
		);
	};

	const deleteAreaMarketingObject = (values) => {
		return handleApi(apiClient.areaMarketingController.deleteFolder(values.id));
	};

	const addAreaMarketingVideo = (values) => {
		const body: AreaObjectCreateRequestDTO = values;
		body.title = values.videoTitle;
		body.link = values.videoLink;
		body.parent_folder_id = currentFolder.id;
		body.type = AreaObjectTypeEnum.VIDEO_URL;

		return handleApi(apiClient.areaMarketingController.postFolder(body));
	};

	const uploadAreaMarketingFileModal = async (values) => {
		const body: AreaObjectCreateRequestDTO = {} as AreaObjectCreateRequestDTO;
		body.title = values.file.name;
		body.parent_folder_id = currentFolder.id;
		body.type = AreaObjectTypeEnum.FILE;
		body.can_download = values.can_download;

		const returnedValue = await handleApi(
			apiClient.areaMarketingController.postFolder(body)
		);
	};

	// handle functions
	const handleOpenFolder = (folder) => {
		navigate(`/area-marketing/${folder.id}`);
	};

	const handleMouseEnter = (folder) => {
		setViewFolderEdit({
			is_visible: true,
			id: folder.id,
		});
	};

	const handleMouseLeave = (folder) => {
		setViewFolderEdit({
			is_visible: false,
			id: folder.id,
		});
	};

	const handleDeleteObject = (object) => {
		showModal('deleteAreaMarketingObject', { object });
	};

	const handleEditObject = (object) => {
		if (object.type === 'folder') {
			showModal('editAreaMarketingFolder', {
				id: object.id,
				folderName: object.title,
				type: object.type,
			});
		} else if (object.type === 'video_url') {
			showModal('editAreaMarketingVideo', {
				id: object.id,
				videoTitle: object.title,
				videoLink: object.link,
				type: object.type,
			});
		}
	};

	const handleBreadcrumbClick = (breadcrumbItem) => {
		if (breadcrumbItem.title === 'root') {
			navigate(`/area-marketing`);
		} else {
			navigate(`/area-marketing/${breadcrumbItem.id}`);
		}
	};

	const debouncedHandleSearch = _.debounce(
		(searchValue) => {
			setLoadingSearch(true);
			handleApi(
				apiClient.areaMarketingController.searchQuery({ search: searchValue })
			)
				.then((res) => {
					setIsSearchActive(true);
					if (searchValue) {
						setSearchResult(res);
					}
					setLoadingSearch(false);
				})
				.catch((err) => {
					setError(err);
					setLoadingSearch(false);
					toaster.error(err.message || i18next.t('error_fetching_data'));
				});
		},
		700,
		{ trailing: true }
	);

	const handleSearch = (e) => {
		const searchValue = e.target.value;
		setSearch(searchValue);
		if (searchValue) {
			debouncedHandleSearch(searchValue);
		} else {
			setIsSearchActive(false);
		}
	};

	const handleResetSearch = () => {
		setSearch('');
		setIsSearchActive(false);
	};

	const handleViewLayout = () => {
		setViewGrid((prev) => !prev);
		localStorage.setItem('viewGrid', JSON.stringify(!viewGrid));
	};

	useEffect(() => {
		if (!id) {
			handleApi(apiClient.areaMarketingController.getRootMarketingFolder())
				.then((res) => {
					setLoading(false);
					setCurrentFolder(res);
				})
				.catch((err) => {
					setError(err);
					setLoading(false);
					toaster.error(
						err.message || i18next.t('error_loading_marketing_area_data')
					);
				});
		} else {
			handleApi(apiClient.areaMarketingController.getSingleFolder(id))
				.then((res) => {
					setLoading(false);
					setCurrentFolder(res);
				})
				.catch((err) => {
					setError(err);
					setLoading(false);
					toaster.error(
						err.message || i18next.t('error_loading_marketing_area_data')
					);
				});
		}
	}, [id]);

	useEffect(() => {
		// check if there are previous folders and add them to the breadcrumbItems
		// these will be mapped and rendered divided by slash (/) on top of the page
		const newBreadcrumbs: { id: string; title: string }[] = (
			currentFolder.prev_folders ?? []
		).map((folder) => {
			return { id: folder.id.toString(), title: folder.title };
		});
		if (newBreadcrumbs && newBreadcrumbs.length > 0) {
			setBreadcrumbsItems(newBreadcrumbs);
		} else setBreadcrumbsItems(initialBreadcrumbs);
	}, [currentFolder]);

	return (
		<div className="area-container">
			<Container fluid>
				{breadcrumbItems.length > 1 ? (
					<Breadcrumb>
						<Breadcrumb.Item style={{ position: 'relative', top: '-3px' }}>
							{showAllBreadcrumbs ? (
								<Button
									variant="link"
									className="p-0"
									onClick={() => setShowAllBreadcrumbs(false)}
								>
									...showLess
								</Button>
							) : (
								<Button
									variant="link"
									className="p-0"
									onClick={() => setShowAllBreadcrumbs(true)}
								>
									...showAll
								</Button>
							)}
						</Breadcrumb.Item>

						{showAllBreadcrumbs ? (
							<>
								{breadcrumbItems.map((breadcrumbItem) => (
									<Breadcrumb.Item
										key={breadcrumbItem.id}
										linkAs={Link}
										linkProps={{ to: `/area-marketing/${breadcrumbItem.id}` }}
										onClick={() => handleBreadcrumbClick(breadcrumbItem)}
									>
										{breadcrumbItem.title === 'root'
											? 'area-marketing'
											: breadcrumbItem.title}
									</Breadcrumb.Item>
								))}
								<Breadcrumb.Item active>{currentFolder.title}</Breadcrumb.Item>
							</>
						) : (
							<>
								{/* Show only the last 2 folders */}
								{breadcrumbItems.slice(-1).map((breadcrumbItem) => (
									<Breadcrumb.Item
										key={breadcrumbItem.id}
										linkAs={Link}
										linkProps={{ to: `/area-marketing/${breadcrumbItem.id}` }}
										// linkProps={{ to: `/${pathnameArray.slice(0, -1).join('/')}` }}
										onClick={() => handleBreadcrumbClick(breadcrumbItem)}
									>
										{breadcrumbItem.title === 'root'
											? 'area-marketing'
											: breadcrumbItem.title}
									</Breadcrumb.Item>
								))}
								<Breadcrumb.Item active>{currentFolder.title}</Breadcrumb.Item>
							</>
						)}
					</Breadcrumb>
				) : (
					// Only render the last two segments when there are 2 or fewer elements
					<Breadcrumb>
						{breadcrumbItems.map((breadcrumbItem) => (
							<Breadcrumb.Item
								key={breadcrumbItem.id}
								linkAs={Link}
								linkProps={{ to: `/area-marketing/${breadcrumbItem.id}` }}
								onClick={() => handleBreadcrumbClick(breadcrumbItem)}
							>
								{breadcrumbItem.title === 'root'
									? i18next.t('area_marketing')
									: breadcrumbItem.title}
							</Breadcrumb.Item>
						))}
						<Breadcrumb.Item active>
							{/* {currentFolder.title === 'root' ? "" : currentFolder.title} */}
							{breadcrumbItems.length > 0
								? currentFolder.title
								: i18next.t('area_marketing')}
						</Breadcrumb.Item>
					</Breadcrumb>
				)}

				<div className="d-flex justify-content-between mb-4">
					<Col md={6} xl={5} className="d-flex">
						<Form.Control
							type="text"
							id="search"
							size="lg"
							placeholder={`${i18next.t('search')}...`}
							value={search}
							onChange={handleSearch}
							aria-describedby="search AreaMarketing"
						/>
						<div className="vr mx-2" />
						<Button variant="secondary" onClick={handleResetSearch}>
							{t('reset')}
						</Button>
					</Col>

					<div className="d-flex align-items-end">
						<div className="text-end me-3 mb-0.5">
							{viewGrid ? (
								<div onClick={handleViewLayout}>
									<FcGrid className="fs-1 layout-switch-button" />
								</div>
							) : (
								<div onClick={handleViewLayout}>
									<FcList className="fs-1 layout-switch-button" />
								</div>
							)}
						</div>

						<Restricted to={[RoleEnum.MASTER]}>
							<Dropdown className="px-2 align-text-bottom action-dropdown">
								<Dropdown.Toggle className="btn">
									{t('actions')}
								</Dropdown.Toggle>
								<Dropdown.Menu className="position-fixed">
									<Dropdown.Item
										as="button"
										onClick={() => {
											showModal('createAreaMarketingFolder');
											GTMsendEvent({
												event: 'select_actions_marketing',
												evtCat: 'Marketing',
												actionType: 'aggiungi cartella',
											});
										}}
									>
										<BiSolidFolderPlus className="fs-5" /> {t('add_folder')}
									</Dropdown.Item>
									<Dropdown.Item
										as="button"
										onClick={() => {
											showModal('addAreaMarketingVideo');
											GTMsendEvent({
												event: 'select_actions_marketing',
												evtCat: 'Marketing',
												actionType: 'aggiungi video',
											});
										}}
									>
										<BiLogoYoutube className="fs-5" /> {t('add_video')}
									</Dropdown.Item>
									<Dropdown.Item
										as="button"
										onClick={() => {
											showModal('uploadAreaMarketingFileModal');
											GTMsendEvent({
												event: 'select_actions_marketing',
												evtCat: 'Marketing',
												actionType: 'aggiungi file',
											});
										}}
									>
										<BiSolidFolderPlus className="fs-5" /> {t('add_file')}
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</Restricted>
					</div>
				</div>

				<OptionActionModal
					show={isModalVisibile}
					onHide={closeModal}
					modalType={modalType}
					details={modalParams}
					data={[]}
					actionMethods={{
						createAreaMarketingFolder,
						editAreaMarketingFolder,
						deleteAreaMarketingObject,
						addAreaMarketingVideo,
						editAreaMarketingVideo,
						uploadAreaMarketingFileModal,
					}}
				/>

				<DisplayData
					loadingSearch={loadingSearch}
					loading={loading}
					viewGrid={viewGrid}
					data={isSearchActive ? searchResult : currentFolder.data}
					viewFolderEdit={viewFolderEdit}
					handleMouseEnter={handleMouseEnter}
					handleMouseLeave={handleMouseLeave}
					handleDeleteObject={handleDeleteObject}
					handleEditObject={handleEditObject}
					handleOpenFolder={handleOpenFolder}
					flagArea={'AreaMarketing'}
				/>
			</Container>
		</div>
	);
}

export default AreaMarketing;
